import { ApplicationContext } from '../../App';
import {
	PalletizerContext,
	RuntimeContext,
} from '../../context/PalletizerContext';
import { BoxConfigurationProps } from '../Configuration';
import {
	Scroll,
	getBarcodeStartModeEnabled,
	useMachineConfig,
} from '../General';
import { KeyboardTrigger, LayoutContent, SelectTrigger } from '../Layout';
import {
	wrapChangeEventNumber,
	from_unit,
	render_length_unit,
	render_length,
	render_weight_unit,
	renderInputLength,
	renderInputWeight,
	render_weight,
	UNITS,
	getChangeWrapperNumber,
	getChangeWrapperString,
	UseStateObject,
	ConfigSetter,
	startCase,
} from '../shared/shared';
import { v4 as uuidv4 } from 'uuid';
import ContentItem, {
	ButtonProps,
	ContentItemProps,
	useConfirm,
	ConfirmTrigger,
	ConfirmAction,
	ContentFooter,
} from './ContentItem';
import {
	SummaryItemCell,
	SummaryScroll,
	ItemIterator,
	duplicateConfiguration,
} from './Shared';
import {
	Box_t,
	BoxDimensions,
	LabelSide,
	LabelNames,
	Product_t,
} from 'blackbox';
import { Application, UnitsSystem } from 'libapi';
import React, { useState, useMemo, useContext, useEffect } from 'react';
import scannerGif from '../../images/barcode_scan.gif';
import Button from '../PendantButton';
import BoxScene from '../three/BoxScene';
import { BoxRenderProps } from '../three/utils';
import { useAssets } from '../../context/AssetsContext';

const USE_PRODUCT_SPECIFICATIONS = false;

enum BoxSteps {
	Dimensions,
	Summary,
}

type BoxSetter = ConfigSetter<Box_t[]>;

interface BoxesHook {
	Step: UseStateObject<BoxSteps>;
	EditingBox: UseStateObject<Box_t>;
	EditingIndex: UseStateObject<number | null>;
	StartEditBox: (pIndex: number) => void;
	LeftButton: ButtonProps;
	RightButton: ButtonProps;
	TriggerConfirm: ConfirmTrigger;
	ConfirmProps: ConfirmAction | undefined;
}

const useBoxes = (
	pAllBoxes: Box_t[],
	pSetBoxes: BoxSetter,
	pHandleBack: () => void,
	pHandleNext: () => void,
	disableNext: boolean,
	unitsSystem: UnitsSystem,
	requireWeight: boolean
): BoxesHook => {
	const app = useContext(ApplicationContext) as Application;
	const { lMachineConfig } = useMachineConfig(app);
	let lSlipSheetEnabled = false;
	lMachineConfig?.Bays.forEach(bay => {
		bay.SlipSheetMags.forEach(m => {
			lSlipSheetEnabled = m || lSlipSheetEnabled;
		});
	});

	const lBarCodeScannerStartMode = getBarcodeStartModeEnabled();

	const pNeedsSizeChangeConfirm = !disableNext;
	const [lCurrentStep, lSetCurrentStep] = useState<BoxSteps>(BoxSteps.Summary),
		[lTriggerConfirm, lConfirmProps] = useConfirm([lCurrentStep]),
		[lEditingBox, lSetEditingBox] = useState<Box_t>(
			getDefaultBox(pAllBoxes.length)
		),
		[lEditingIndex, lSetEditingIndex] = useState<number | null>(null),
		lStartEdit = function (pIndex: number) {
			if (pIndex >= 0) {
				lSetEditingBox(pAllBoxes[pIndex]);
				lSetEditingIndex(pIndex);
			} else {
				lSetEditingIndex(null);
				lSetEditingBox(getDefaultBox(pAllBoxes.length));
			}
			lSetCurrentStep(BoxSteps.Dimensions);
		},
		lLeftButton: ButtonProps = {
			name: lCurrentStep === BoxSteps.Summary ? '' : 'Back',
			action: () => {
				switch (lCurrentStep) {
					case BoxSteps.Summary:
						pHandleBack();
						break;
					case BoxSteps.Dimensions:
						lSetCurrentStep(BoxSteps.Summary);
						break;
				}
			},
			enabled: lCurrentStep !== BoxSteps.Summary,
		},
		didBoxChange = function () {
			if (lEditingIndex === null) return;
			const lOldBox = pAllBoxes[lEditingIndex];
			const lCompareDim = function (lDim: keyof BoxDimensions) {
				return lOldBox.dimensions[lDim] === lEditingBox.dimensions[lDim];
			};
			return !lCompareDim('length') || !lCompareDim('width');
		},
		lRightButton: ButtonProps = {
			name:
				lCurrentStep === BoxSteps.Summary
					? lSlipSheetEnabled
						? 'Create a Slip Sheet'
						: 'Create a Pallet'
					: 'Save',
			action: () => {
				if (lCurrentStep === BoxSteps.Summary) {
					if (pAllBoxes.length > 0) pHandleNext();
					return;
				}
				if (lEditingIndex !== null) {
					let b = [...pAllBoxes];
					const lDidChange = didBoxChange();
					b[lEditingIndex] = {
						...lEditingBox,
						changed: lDidChange,
					};
					const lBox = b[lEditingIndex];
					if (pNeedsSizeChangeConfirm && lDidChange) {
						lTriggerConfirm(function () {
							pSetBoxes(b);
							lSetCurrentStep(BoxSteps.Summary);
						}, `Changing dimensions will remove layouts and stacks that use ${lBox.name}.`);
						return;
					}
					pSetBoxes(b);
					lSetCurrentStep(BoxSteps.Summary);
					return;
				} else {
					const nbs = [...pAllBoxes, lEditingBox];
					pSetBoxes(nbs);
					lSetEditingIndex(nbs.length - 1);
				}
				// short circuit to summary after first step.
				lSetCurrentStep(BoxSteps.Summary);
			},
			enabled: lCurrentStep === BoxSteps.Summary ? pAllBoxes.length > 0 : true,
			hidden: disableNext && lCurrentStep === BoxSteps.Summary,
			restrictionCheck: function () {
				if (lCurrentStep !== BoxSteps.Summary && lEditingBox !== undefined) {
					if ((lEditingBox.weight || 0) <= 0 && requireWeight) {
						return 'Box weight cannot be zero';
					} else if (
						lEditingBox.dimensions.height <= 0 ||
						lEditingBox.dimensions.length <= 0 ||
						lEditingBox.dimensions.width <= 0
					) {
						return 'Box dimensions must be greater than zero';
					}
				}

				const { product } = lEditingBox;
				if (
					USE_PRODUCT_SPECIFICATIONS &&
					(!product ||
						(product &&
							product.quantity * product.height * product.width * product.length ===
								0))
				) {
					return 'Product information must be specified';
				}

				if (lBarCodeScannerStartMode && lEditingBox.skuId.length === 0) {
					return 'SKU cannot be empty';
				}
				return void 0;
			},
		};
	return {
		Step: { Current: lCurrentStep, Set: lSetCurrentStep },
		EditingBox: { Current: lEditingBox, Set: lSetEditingBox },
		EditingIndex: { Current: lEditingIndex, Set: lSetEditingIndex },
		StartEditBox: lStartEdit,
		LeftButton: lLeftButton,
		RightButton: lRightButton,
		TriggerConfirm: lTriggerConfirm,
		ConfirmProps: lConfirmProps,
	};
};

export default function BoxSize({
	allBoxes,
	instructionNumber,
	setBoxes,
	unitsSystem,
	handleBack,
	handleNext,
	disableNext,
	requireWeight,
	showLegend,
	triggerKeyboard,
	triggerSelect,
}: BoxConfigurationProps) {
	const lBoxesHook = useBoxes(
		allBoxes,
		setBoxes,
		handleBack,
		handleNext,
		disableNext,
		unitsSystem,
		requireWeight
	);

	const startEdit = (pIndex: number) => () => lBoxesHook.StartEditBox(pIndex);
	const editingBox = lBoxesHook.EditingBox.Current;
	const setEditingBox = lBoxesHook.EditingBox.Set;
	const removeBox = (pBoxIndex: number) => () =>
		lBoxesHook.TriggerConfirm(() => {
			let cp = [...allBoxes];
			cp[pBoxIndex].deleted = true;
			setBoxes(cp);
		}, `Are you sure you want to delete ${allBoxes[pBoxIndex].name}?`);
	const lItemIterator = useMemo(
		function (): ItemIterator {
			return {
				pGet: function (
					pStartIndex: number,
					pEndOffset: number,
					pSearchFilter: string | undefined
				) {
					let lBoxes = [...allBoxes].map(function (pBox, pBoxIndex) {
						return [pBox, pBoxIndex] as [Box_t, number];
					});
					if (pSearchFilter) {
						const lRegExp = new RegExp(pSearchFilter, 'i');
						lBoxes = lBoxes.filter(([pBox, pBoxIndex]) => lRegExp.test(pBox.name));
					}
					const lItemCount = lBoxes.length;
					const disableDelete = lItemCount === 1;
					return [
						lBoxes
							.splice(pStartIndex, pEndOffset)
							.map(function (pBoxPair: [Box_t, number], pRelativeIndex: number) {
								const lIndex = pBoxPair[1],
									boxCellProps: BoxProps = {
										box: pBoxPair[0],
										unitsSystem: unitsSystem,
										startEdit: startEdit(lIndex),
										handleDelete: removeBox(lIndex),
										handleCopy: duplicateConfiguration(lIndex, allBoxes, setBoxes),
										showWeight: requireWeight,
										disableDelete,
									};
								return <BoxCell {...boxCellProps} key={lIndex} />;
							}),
						lItemCount,
					];
				},
				triggerKeyboard: triggerKeyboard,
			};
		},
		[allBoxes]
	);

	let instruction: string;
	const sub_step_count = 5,
		sub_step = lBoxesHook.Step.Current + 1;

	switch (lBoxesHook.Step.Current) {
		case BoxSteps.Dimensions:
			instruction = 'Enter Box Dimensions';
			const createBoxProps: CreateNewBoxProps = {
				box: editingBox,
				unitsSystem: unitsSystem,
				LeftButton: lBoxesHook.LeftButton,
				RightButton: lBoxesHook.RightButton,
				updateBox: setEditingBox,
				instruction,
				instructionNumber,
				sub_step,
				sub_step_count,
				ConfirmProps: lBoxesHook.ConfirmProps,
				requireWeight,
				showLegend,
				triggerKeyboard,
				triggerSelect,
			};
			return <CreateNewBox {...createBoxProps} />;
		case BoxSteps.Summary: {
			instruction = 'Create And Edit Boxes';
			const AddButton: ButtonProps = {
				name: 'Add new box',
				action: startEdit(-1),
			};
			const summaryProps = {
				...lItemIterator,
				instruction,
				AddButton,
				RightFooterButton: lBoxesHook.RightButton,
				LeftFooterButton: lBoxesHook.LeftButton,
				ConfirmFooter: lBoxesHook.ConfirmProps,
			};
			return (
				<div className="full-size flex-center align-center">
					<SummaryScroll {...summaryProps} />
				</div>
			);
		}
	}
}

interface BoxProps {
	box: Box_t;
	startEdit: () => void;
	handleDelete: () => void;
	handleCopy: () => void;
	unitsSystem: UnitsSystem;
	showWeight: boolean;
	disableDelete?: boolean;
}

function BoxCell({
	box,
	startEdit,
	handleDelete,
	handleCopy,
	unitsSystem,
	showWeight,
	disableDelete,
}: BoxProps) {
	const { width, length, height } = box.dimensions;
	const assets = useAssets();

	const brp: BoxRenderProps = {
		...box.dimensions,
		labelSide: box.labelSide,
		hasLabels: false,
	};

	let properties =
		`${render_length(width, unitsSystem)} x ${render_length(
			length,
			unitsSystem
		)} x ${render_length(height, unitsSystem)} ${render_length_unit(
			unitsSystem
		)}` +
		(showWeight
			? `, ${render_weight(box.weight || 0, unitsSystem)} ${render_weight_unit(
					unitsSystem
			  )}`
			: ``);

	return (
		<SummaryItemCell
			name={box.name}
			properties={properties}
			trashAction={handleDelete}
			editAction={startEdit}
			duplicateAction={handleCopy}
			disableDelete={disableDelete}
		>
			{assets.texturesLoaded ? (
				<BoxScene
					{...brp}
					showFaceLabels={false}
					zoom={0.7}
					labelPosition={box.labelSide}
					enableControls={false}
				/>
			) : (
				<div />
			)}
		</SummaryItemCell>
	);
}

//---------------Box Setup Screen---------------
export interface CoordinateItemProps {
	pPromptItem: string /* box/pallet */;
	name: string;
	axis_str: string;
	value: number;
	units_system: UnitsSystem;
	setter: (val: number) => void;
	triggerKeyboard: KeyboardTrigger;
}

export function CoordinateItem({
	pPromptItem,
	name,
	value,
	units_system,
	setter,
	triggerKeyboard,
}: CoordinateItemProps) {
	const lInputHandlers = getChangeWrapperNumber(function (pNewValue: number) {
			if (UnitsSystem.IMPERIAL === units_system) {
				pNewValue = from_unit(pNewValue, UNITS.inches);
			}
			setter(pNewValue);
		}),
		lTriggerKeyboard = function () {
			if (triggerKeyboard) {
				triggerKeyboard(
					`Enter ${pPromptItem} ${name.toLowerCase()} dimension (${render_length_unit(
						units_system
					)})`,
					renderInputLength(value, units_system),
					lInputHandlers.FromString
				);
			}
		};
	return (
		<div className="full-width-minus-2pad flex-col align-start double-margin-bottom">
			<span className="bold-font margin-bottom">
				{name + ` (${render_length_unit(units_system)})`}
			</span>

			<input
				className="full-width"
				type="number"
				value={renderInputLength(value, units_system)}
				onChange={lInputHandlers.Event}
				onClick={lTriggerKeyboard}
				step={0.01}
			/>
		</div>
	);
}

interface WeightItemProps {
	value: number;
	units_system: UnitsSystem;
	setter: (val: number) => void;
	triggerKeyboard: KeyboardTrigger;
}

function WeightItem({
	value,
	units_system,
	setter,
	triggerKeyboard,
}: WeightItemProps) {
	const lInputHandlers = getChangeWrapperNumber(function (pNewValue: number) {
			if (UnitsSystem.IMPERIAL === units_system) pNewValue = pNewValue / 2.20462;
			setter(pNewValue);
		}),
		lTriggerKeyboard = function () {
			if (triggerKeyboard) {
				triggerKeyboard(
					`Enter box weight (${render_weight_unit(units_system)})`,
					renderInputWeight(value, units_system),
					lInputHandlers.FromString
				);
			}
		};

	return (
		<div className="full-width-minus-2pad flex-col align-start double-margin-bottom">
			<div className="bold-font margin-bottom">
				<span>{`Weight (${render_weight_unit(units_system)})`}</span>
			</div>
			<input
				className="full-width"
				type="number"
				value={renderInputWeight(value, units_system)}
				onChange={lInputHandlers.Event}
				onClick={lTriggerKeyboard}
			/>
		</div>
	);
}

interface CreateNewBoxProps {
	box: Box_t;
	LeftButton: ButtonProps;
	RightButton: ButtonProps;
	updateBox: (b: Box_t) => void;
	instructionNumber: number;
	instruction: string;
	sub_step: number;
	sub_step_count: number;
	ConfirmProps: ConfirmAction | undefined;
	unitsSystem: UnitsSystem;
	requireWeight: boolean;
	showLegend?: boolean;
	triggerKeyboard: KeyboardTrigger;
	triggerSelect: SelectTrigger;
}

function CreateNewBox({
	instructionNumber,
	box,
	LeftButton,
	RightButton,
	updateBox,
	sub_step,
	sub_step_count,
	instruction,
	ConfirmProps,
	unitsSystem,
	requireWeight,
	showLegend,
	triggerKeyboard,
	triggerSelect,
}: CreateNewBoxProps) {
	const app = useContext(ApplicationContext) as Application;
	const lBarCodeScannerStartMode = getBarcodeStartModeEnabled();
	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;

	let curr_label_side = LabelSide.Left;
	if ('labelSide' in box) curr_label_side = box.labelSide;

	const updateProductFieldValue = (pKey: keyof Product_t, pValue: number) => {
			if (UnitsSystem.IMPERIAL === unitsSystem && pKey !== 'quantity')
				pValue = from_unit(pValue, UNITS.inches);
			const lCopy = { ...box };
			lCopy.product = lCopy.product || {
				width: 0,
				length: 0,
				height: 0,
				quantity: 0,
			};
			if (lCopy.product) lCopy.product[pKey] = pValue;
			updateBox(lCopy);
		},
		updateProductField = function (pKey: keyof Product_t) {
			return wrapChangeEventNumber(function (pNumber) {
				updateProductFieldValue(pKey, pNumber);
			});
		},
		lProductHandlers = (pKey: keyof Product_t) =>
			getChangeWrapperNumber(function (pNewValue: number) {
				updateProductFieldValue(pKey, pNewValue);
			}),
		lTriggerKeyboardProduct = function (pKey: keyof Product_t) {
			if (triggerKeyboard) {
				triggerKeyboard(
					`Enter product ${pKey} ${
						pKey !== 'quantity' ? '(' + render_length_unit(unitsSystem) + ')' : ''
					}`,
					String(box.product?.[pKey] || 0),
					lProductHandlers(pKey).FromString
				);
			}
		};

	const lCurrentAltLabel = box.labelSideAlt || LabelSide.None;
	const updateLabelSide = (n: number) => {
			updateBox({ ...box, labelSide: n as LabelSide });
		},
		updateAltLabelSide = (n: number) => {
			updateBox({ ...box, labelSideAlt: n as LabelSide });
		},
		lNameHandlers = getChangeWrapperString(function (pNewName: string) {
			updateBox({ ...box, name: pNewName });
		}),
		updateWeight = (weight: number) => {
			updateBox({ ...box, weight });
		},
		lTriggerKeyboardName = function () {
			if (triggerKeyboard) {
				triggerKeyboard(
					'Enter a name for your Box:',
					box.name,
					lNameHandlers.Direct
				);
			}
		},
		updateDimension = (dim: keyof BoxDimensions) => (val: number) => {
			const dims = { ...box.dimensions };
			dims[dim] = val;
			updateBox({ ...box, dimensions: dims });
		},
		contentItemProps: ContentItemProps = {
			instruction,
			RightButton,
			LeftButton,
			instructionNumber,
			sub_step,
			sub_step_count,
			confirmAction: ConfirmProps,
		},
		lTriggerLabelSelect = function (pEvent: React.SyntheticEvent) {
			if (!triggerSelect) return;
			pEvent.preventDefault();
			triggerSelect(
				'Select Label Side',
				LabelNames,
				updateLabelSide,
				curr_label_side
			);
		},
		lTriggerAltLabelSelect = function (pEvent: React.SyntheticEvent) {
			if (!triggerSelect) return;
			pEvent.preventDefault();
			triggerSelect(
				'Select Alternate Label Side',
				LabelNames,
				updateAltLabelSide,
				lCurrentAltLabel
			);
		};

	const brp: BoxRenderProps = {
		...box.dimensions,
		labelSide: curr_label_side,
		showColor: showLegend,
		hasLabels: true,
	};
	const lProductDimensionKeys: (keyof BoxDimensions)[] = [
		'width',
		'length',
		'height',
	];

	// Bar code related
	const { pendingSKUScan, invalidSKUScan } = lRuntimeContext;
	// Robot mode might be fault while scanning so need to track seperately here.
	const [lIsScanning, lSetIsScanning] = useState<boolean>(false);

	const lGetInvalid = () => Object.values(invalidSKUScan)[0];
	useEffect(() => {
		console.log('Invalid updated!', invalidSKUScan);
		if (!lIsScanning) return;
		// Just take first value in invalid/pendingSKUScan (should only ever be 1)
		const invalid = lGetInvalid();
		if (!!invalid && lIsScanning) {
			// Tell user invalid sku was scanned, make them confirm.
			// unless its the current box then just close the dialog
			if (invalid === box.skuId) {
				lSetIsScanning(false);
				app.stop();
				app.clearInvalidSKUScan(invalidSKUScan);
			}
			return;
		}

		const vs = Object.values(pendingSKUScan);
		const v = vs[0];
		if (!!v) {
			updateBox({ ...box, skuId: v });
			lSetIsScanning(false);
			app.stop();
			app.clearPendingSKUScan(pendingSKUScan);
		}
	}, [pendingSKUScan, invalidSKUScan]);

	return (
		<ContentItem {...contentItemProps}>
			<>
				<div className="full-size flex-center align-start color-blue">
					<div className="grid-col-70 flex-between full-height full-width">
						<div className="BoxConfigurator">
							<BoxScene
								length={brp.length}
								width={brp.width}
								height={brp.height}
								labelPosition={brp.labelSide || 0}
								showFaceLabels={true}
								zoom={0.7}
							/>
							<ContentFooter
								RightButton={RightButton}
								LeftButton={LeftButton}
								confirmAction={ConfirmProps}
							/>
						</div>

						<Scroll className="full-size border-left">
							<div className="flex-col align-center full-size">
								<div className="full-width-minus-2pad flex-col align-start double-margin-top double-margin-bottom">
									<span className="bold-font margin-bottom">Box Name</span>
									<input
										className="full-width"
										type="text"
										value={box.name}
										onChange={lNameHandlers.Event}
										onClick={lTriggerKeyboardName}
									/>
								</div>
								{lBarCodeScannerStartMode && (
									<div className="full-width-minus-2pad flex-col align-start double-margin-bottom">
										<span className="bold-font margin-bottom">SKU</span>

										<input
											className="full-width"
											type="text"
											value={box.skuId}
											onChange={() => {}}
											onClick={() => {
												lSetIsScanning(true);
												app.clearPendingSKUScan(pendingSKUScan); // This just for safety
												app.startScanning(true);
											}}
										/>
									</div>
								)}

								<CoordinateItem
									pPromptItem="box"
									name={'Width'}
									axis_str="X"
									value={box.dimensions.width}
									units_system={unitsSystem}
									setter={updateDimension('width')}
									triggerKeyboard={triggerKeyboard}
								/>
								<CoordinateItem
									pPromptItem="box"
									name={'Length'}
									axis_str="Y"
									value={box.dimensions.length}
									units_system={unitsSystem}
									setter={updateDimension('length')}
									triggerKeyboard={triggerKeyboard}
								/>
								<CoordinateItem
									pPromptItem="box"
									name={'Height'}
									axis_str="Z"
									value={box.dimensions.height}
									units_system={unitsSystem}
									setter={updateDimension('height')}
									triggerKeyboard={triggerKeyboard}
								/>
								{requireWeight && (
									<WeightItem
										value={box.weight || 0}
										units_system={unitsSystem}
										setter={updateWeight}
										triggerKeyboard={triggerKeyboard}
									/>
								)}
								{showLegend && (
									<div className="full-width-minus-2pad flex-col align-start double-margin-bottom">
										<span className="bold-font margin-bottom">Labels</span>
										<div className="width-100">
											<div className="flex-start">
												<select
													value={curr_label_side}
													onChange={wrapChangeEventNumber(updateLabelSide)}
													onMouseDown={lTriggerLabelSelect}
												>
													{LabelNames.map((name: string, i: number) => {
														return (
															<option key={i} value={i}>
																{name}
															</option>
														);
													})}
												</select>
												<div className="padding"></div>
												<select
													value={lCurrentAltLabel}
													onChange={wrapChangeEventNumber(updateAltLabelSide)}
													onMouseDown={lTriggerAltLabelSelect}
													disabled={curr_label_side === LabelSide.None}
													style={{ marginLeft: '8pt' }}
												>
													{LabelNames.map((name: string, i: number) => {
														return (
															<option key={i} value={i}>
																{name}
															</option>
														);
													})}
												</select>
											</div>
										</div>
									</div>
								)}

								{USE_PRODUCT_SPECIFICATIONS && (
									<div className="full-width-minus-2pad flex-col align-start double-margin-bottom">
										<span className="bold-font margin-bottom">
											Contents Specification
										</span>
										<div className="full-width flex-row flex-between align-center half-margin-top">
											<span>Quantity</span>
											<input
												className="half-padding width-30"
												type="number"
												value={box.product?.quantity || 0}
												onChange={updateProductField('quantity')}
												onClick={() => lTriggerKeyboardProduct('quantity')}
												step={0.01}
											/>
										</div>
										{lProductDimensionKeys.map((pDim, pIndex) => {
											return (
												<div
													className="full-width flex-row flex-between align-center half-margin-top"
													key={pIndex}
												>
													<span>{`${startCase(pDim)} (${render_length_unit(
														unitsSystem
													)})`}</span>
													<input
														className="half-padding width-30"
														type="number"
														value={
															box.product?.[pDim]
																? renderInputLength(box.product[pDim], unitsSystem)
																: 0
														}
														onChange={updateProductField(pDim)}
														onClick={() => lTriggerKeyboardProduct(pDim)}
														step={0.01}
													/>
												</div>
											);
										})}
									</div>
								)}
								<div className="padding"></div>
								<div className="padding"></div>
								<div className="padding"></div>
							</div>
						</Scroll>
					</div>
				</div>
				{lIsScanning && (
					<LayoutContent pTab={{ Name: '', IconClass: '' }} dialog={true}>
						<div className="full-size">
							{(function () {
								const lInvalid = lGetInvalid();
								if (!!lInvalid) {
									return (
										<div className="full-size flex-col align-center flex-around">
											<h3 className="font-7 color-blue bold-font">Invalid SKU</h3>

											<span className="font-5 color-blue ">
												The SKU <b>'{lInvalid}'</b> is already associated with a box.
											</span>
											<Button
												pAction={() => {
													lSetIsScanning(false);
													app.clearInvalidSKUScan(invalidSKUScan);
													app.stop();
												}}
												className="width-20"
											>
												<span className="font-5">Cancel</span>
											</Button>
										</div>
									);
								} else {
									return (
										<div className="full-size flex-col align-center flex-around">
											<div className="flex-col align-center">
												<h3 className="font-7 color-blue bold-font">Scan a new SKU</h3>
												<div className="padding" />
												<span className="font-5 color-blue ">Looking for a barcode...</span>
											</div>
											<img
												src={scannerGif}
												alt="scan barcode"
												className="width-16 square"
											/>
											<Button
												pAction={() => {
													lSetIsScanning(false);
													app.stop();
												}}
												className="width-20"
											>
												<span className="font-5">Cancel</span>
											</Button>
										</div>
									);
								}
							})()}
						</div>
					</LayoutContent>
				)}
			</>
		</ContentItem>
	);
}

function getDefaultBox(lCurrentNumBoxes: number): Box_t {
	return {
		name: 'Box ' + String(lCurrentNumBoxes + 1),
		skuId: '',
		id: String(uuidv4()),
		dimensions: { length: 300, height: 150, width: 300 },
		pickLocation: [0, 0, 1500, 0],
		boxDetection: -1,
		detectionEnabled: false,
		pickFromStack: false,
		labelSide: LabelSide.None,
		openTop: false,
		accRatio: 1.0,
	};
}
